import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { Button, ChipCloud, Icons, LockBadge } from "../../../../ds";
import { DateTimeHelper } from "../../../../lib/utils/DateTimeHelper";
import { LockMark } from "../../../locks/models/LockDTO";
import { PageMetadataTag } from "../../models/PageMetadata";

export interface PageTrailItem {
    text: string;
    to?: string;
}

interface IPageHeaderProps {
    title: string;
    author: string;
    date: string;
    /** Effective expedition of the page (metadata cascade), when it has one. */
    expedition?: string;
    /** Effective tags of the page (own, inherited or inferred). */
    tags?: PageMetadataTag[];
    /** The page's lock badge (own or inherited), when it is locked. */
    lock?: LockMark;
    /** Space › ancestors above the title (the page itself is the h1). */
    trail?: PageTrailItem[];
    /** The actions row (See history, Edit Page; Open in Ark / Refresh when embedded). */
    children?: React.ReactNode;
}

export function tagTitle(tag: PageMetadataTag): string {
    if (tag.source === "own") return tag.name;
    const confidence = tag.confidence !== undefined ? ` ${Math.round(tag.confidence * 100)}%` : "";
    return `${tag.name} (${tag.source}${confidence})`;
}

/** "Tags · 4": a secondary button that opens a popover with the tag chips (hover: source and confidence). */
function TagsButton(props: { tags: PageMetadataTag[] }) {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    const popoverId = useId();

    useEffect(() => {
        if (!open) return;
        const onPointer = (ev: MouseEvent) => {
            if (ref.current && !ref.current.contains(ev.target as Node)) setOpen(false);
        };
        const onKey = (ev: KeyboardEvent) => {
            if (ev.key === "Escape") setOpen(false);
        };
        document.addEventListener("mousedown", onPointer);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onPointer);
            document.removeEventListener("keydown", onKey);
        };
    }, [open]);

    return (
        <div className="ark-doc-tags" ref={ref}>
            <Button
                type="secondary"
                size="sm"
                icon={<Icons.tag size={14} />}
                text={`Tags · ${props.tags.length}`}
                title="Show tags"
                aria-label={`Show tags (${props.tags.length})`}
                aria-expanded={open}
                aria-controls={open ? popoverId : undefined}
                onClick={() => setOpen((value) => !value)}
            />
            {open ? (
                <div className="ark-doc-tags-popover" id={popoverId} role="dialog" aria-label="Tags">
                    <ChipCloud
                        aria-label="Tags"
                        mono={false}
                        max={Infinity}
                        items={props.tags.map((tag) => ({
                            label: tag.name,
                            title: tagTitle(tag),
                            tone: tag.source === "own" ? "accent" : "neutral",
                        }))}
                    />
                </div>
            ) : null}
        </div>
    );
}

/**
 * The reader's page header (board "Ark-Page"): space › ancestors, the h1 with the lock badge, the meta line (last editor ·
 * updated …), the expedition pill and the tags popover, then the actions row.
 */
export function PageHeader(props: IPageHeaderProps) {
    const tags = props.tags ?? [];
    const hasTags = tags.length > 0;
    const lock = props.lock;

    return (
        <header className="ark-doc-head">
            {props.trail && props.trail.length > 0 ? (
                <nav className="ark-doc-trail" aria-label="Page path">
                    <ol>
                        {props.trail.map((item, i) => (
                            <li key={`${i}-${item.text}`}>
                                {item.to ? <Link to={item.to}>{item.text}</Link> : <span>{item.text}</span>}
                                <Icons.chevronRight size={12} />
                            </li>
                        ))}
                        <li aria-current="page"><span>{props.title}</span></li>
                    </ol>
                </nav>
            ) : null}
            <div className="ark-doc-title-row">
                <h1 className="ark-doc-title">{props.title}</h1>
                {lock ? (
                    <span className="ark-doc-lock">
                        <LockBadge state={lock.own ? "own" : "inherited"} by={lock.lockTitle} />
                        <span aria-hidden="true">{lock.own ? "Locked" : "Locked through a parent"}</span>
                    </span>
                ) : null}
            </div>
            <div className="ark-doc-meta">
                {props.author?.trim() ? (
                    <>
                        <span className="ark-doc-author">{props.author}</span>
                        <span className="ark-doc-dot" aria-hidden="true">·</span>
                    </>
                ) : null}
                <span>updated {DateTimeHelper.formatDateTimeAsTimeAgo(props.date)}</span>
                {props.expedition ? (
                    <span className="ark-doc-expedition" title={props.expedition}>
                        <span className="ark-doc-expedition-label">Expedition</span>
                        <strong>{props.expedition}</strong>
                    </span>
                ) : null}
                {hasTags ? <TagsButton tags={tags} /> : null}
            </div>
            {props.children ? <div className="ark-doc-actions">{props.children}</div> : null}
        </header>
    );
}
